import * as React from 'react';
import { useMemo, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client';
import UpdateReportWorkspace from '@graphql/mutations/UpdateReportWorkspace';
import AllWorkspacesQuery from '@graphql/queries/AllWorkspacesQuery';
import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import MaterialButton from 'components/MaterialButton';
import { useTranslation } from 'react-i18next';

interface WorkspaceOption {
  id: number;
  name: string;
}

interface Props {
  report: reports.AllReportsQueryRow;
  onClose: () => void;
  onSaved: () => void;
}

const EditReportWorkspaceModal: React.FC<Props> = ({
  report,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<WorkspaceOption | null>(null);

  // Only fetched while the dialog is open
  const { data, loading } = useQuery<{
    allProducers: { nodes: WorkspaceOption[] };
  }>(AllWorkspacesQuery);
  const [updateWorkspace, { loading: saving }] = useMutation(
    UpdateReportWorkspace,
  );

  const options = useMemo(
    () =>
      [...(data?.allProducers?.nodes ?? [])]
        .map(({ id, name }) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [data],
  );

  const handleSave = async () => {
    if (!selected) return;
    await updateWorkspace({
      variables: { jobId: report.jobId, workspaceId: selected.id },
    });
    onSaved();
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('reportsQa.edit.workspaceTitle')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {t('reportsQa.edit.reportId', { reportId: report.reportId })}
        </Typography>
        <Autocomplete
          id="select-report-update-workspace"
          loading={loading}
          options={options}
          value={selected}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_, workspace) => setSelected(workspace)}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              margin="dense"
              label={t('reportsQa.edit.selectWorkspace')}
              placeholder={report.producerByWorkspaceId?.name}
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <MaterialButton variant="outlined" soft onClick={onClose}>
          {t('general.cancel')}
        </MaterialButton>
        <MaterialButton
          variant="outlined"
          soft
          teal
          disabled={!selected || saving}
          onClick={handleSave}
        >
          {t('reportsQa.edit.save')}
        </MaterialButton>
      </DialogActions>
    </Dialog>
  );
};

export default EditReportWorkspaceModal;
